import { startCase } from "lodash";
import { FC, useEffect, useState } from "react";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { SpecialtyGroupException, SpecialtyGroupExceptionDisplay } from "@app/exceptions/interfaces";
import {
  SpecialtyGroupExceptionGrade, SpecialtyGroupExceptionReviewStatus,
  SpecialtyGroupExceptionReviewStatusLabels, SpecialtyGroupExceptionStatus
} from "@app/exceptions/enums";
import { formatDateTime, formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import { Timeline } from "@app/shared/timeline/Timeline";
import { TimelineEvent } from "@app/shared/timeline/TimelineEvent";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors";

interface SpecialtyGroupExceptionTimelineProps {
  projectId?: number;
  regionSetSpecialtyGroupId?: number;
  specialtyGroupException?: SpecialtyGroupException;
}

export const SpecialtyGroupExceptionHistoryTimeline: FC<SpecialtyGroupExceptionTimelineProps> = (props: SpecialtyGroupExceptionTimelineProps) => {
  const specialtyGroupExceptionsService = new ExceptionsService();

  const [specialtyGroupExceptionHistories, setSpecialtyGroupExceptionHistories] = useState<SpecialtyGroupExceptionDisplay[]>([]);

  useEffect(() => {
    loadSpecialtyExceptionHistories();
  }, []);

  const loadSpecialtyExceptionHistories = async () => {
    let specialtyGroupExceptionHistories: SpecialtyGroupExceptionDisplay[];
    if (props.specialtyGroupException) {
      specialtyGroupExceptionHistories = await specialtyGroupExceptionsService.getSpecialtyGroupExceptionHistory(
        props.specialtyGroupException.customProjectId,
        props.specialtyGroupException.regionSetId,
        props.specialtyGroupException.specialtyGroupCode
      );
    } else {
      specialtyGroupExceptionHistories = await specialtyGroupExceptionsService.getSpecialtyGroupExceptionHistoryByRegionSetSpecialtyGroupId(
        props.projectId,
        props.regionSetSpecialtyGroupId
      );
    }

    setSpecialtyGroupExceptionHistories(specialtyGroupExceptionHistories);
  }

  const displayRationale = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return '---';
    return value ? 'yes' : 'no';
  }

  const renderException = (): React.ReactNode => {
    if (!specialtyGroupExceptionHistories) {
      return null;
    }

    return (
      <>
        <div>
          <Timeline>
            {specialtyGroupExceptionHistories.map((specialtyGroupExceptionHistory) => {
              let icon = <QesIcon iconName={QesIcons.MinusCircle} color={QesColors.QesTertiaryYellow2} description="warning" />;
              switch (specialtyGroupExceptionHistory.specialtyGroupExceptionStatus) {
                case SpecialtyGroupExceptionStatus.Approved:
                  icon = <QesIcon iconName={QesIcons.CheckCircle} color={QesColors.QesPrimaryGreen} description="approved" />;
                  break;
                case SpecialtyGroupExceptionStatus.Denied:
                  icon = <QesIcon iconName={QesIcons.XCircle} color={QesColors.QesPrimaryRed} description="denied" />;
                  break;
                case SpecialtyGroupExceptionStatus.Archived:
                  icon = <QesIcon iconName={QesIcons.TrashV1} color={QesColors.QesPrimaryRed} description="archived" />;
                  break;
              }

              return (
                <TimelineEvent
                  key={`${specialtyGroupExceptionHistory.specialtyGroupExceptionStatus}:${specialtyGroupExceptionHistory
                    .dateUpdated}`}
                  title={`Updated by ${!specialtyGroupExceptionHistory.updatedByUserFullName || specialtyGroupExceptionHistory.updatedByUserFullName === "Quest Analytics" ? "System" : specialtyGroupExceptionHistory.updatedByUserFullName} on`}
                  subtitle={`${formatDateTime(specialtyGroupExceptionHistory.dateUpdated)}`}
                  icon={icon}
                >
                  <div className="container timeline-container">
                    <div className="row mb5">
                      <div className="col-12 col-lg-3">
                        <strong>Status:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {startCase(specialtyGroupExceptionHistory.specialtyGroupExceptionStatus)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Start Date:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.startDate ? formatUnconvertedDateCustom(specialtyGroupExceptionHistory.startDate, "MM/DD/YYYY") : '---'}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Expiration Date:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.endDate ? formatUnconvertedDateCustom(specialtyGroupExceptionHistory.endDate, "MM/DD/YYYY") : '---'}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Grade:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.grade === SpecialtyGroupExceptionGrade.None ? '---' : startCase(specialtyGroupExceptionHistory.grade)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Internal Review Status:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.reviewStatus === SpecialtyGroupExceptionReviewStatus.None ? '---' : SpecialtyGroupExceptionReviewStatusLabels[specialtyGroupExceptionHistory.reviewStatus]}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Q1:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {displayRationale(specialtyGroupExceptionHistory.rationaleAnswers?.exceptionRationale1)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Q2:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {displayRationale(specialtyGroupExceptionHistory.rationaleAnswers?.exceptionRationale2)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Q3:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {displayRationale(specialtyGroupExceptionHistory.rationaleAnswers?.exceptionRationale3)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Q4:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {displayRationale(specialtyGroupExceptionHistory.rationaleAnswers?.exceptionRationale4)}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Comment:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.comment}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Processed:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.isUnprocessed ? 'No' : 'Yes'}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-lg-3">
                        <strong>Exception Narrative:</strong>
                      </div>
                      <div className="col-12 col-lg">
                        {specialtyGroupExceptionHistory.exceptionNarrative}
                      </div>
                    </div>
                    {(specialtyGroupExceptionHistory.supplementaryDocuments ?? []).length > 0 &&
                      (
                        <div className="row">
                          <div className="col-12 col-lg-3">
                            <strong>Attachments:</strong>
                          </div>
                          <div className="col-12 col-lg">
                            <ul>
                              {
                                specialtyGroupExceptionHistory.supplementaryDocuments.map((document) =>
                                  <li key={`${document.specialtyGroupExceptionId}_${document.supplementaryDocumentMetadataId}`}>
                                    {document.documentName}
                                  </li>)
                              }
                            </ul>
                          </div>
                        </div>
                      )}
                    <div className="row">
                          <div className="col-12 col-lg-3">
                            <strong>Include 0% Gains Providers:</strong>
                          </div>
                          <div className="col-12 col-lg">
                            {specialtyGroupExceptionHistory.zeroPercentGainsProvidersComment != null
                              && specialtyGroupExceptionHistory.zeroPercentGainsProvidersComment !== ""
                                ? 'Yes' : 'No'}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12 col-lg-3">
                            <strong>0% Gains Providers Comment:</strong>
                          </div>
                          <div className="col-12 col-lg">
                            {specialtyGroupExceptionHistory.zeroPercentGainsProvidersComment}
                          </div>
                        </div>
                  </div>
                </TimelineEvent>
              );
            })}
          </Timeline>
        </div>
      </>
    );
  };

  return renderException();

}
